import { useMutation, useQuery } from '@tanstack/react-query';
import { Card, Col, DatePicker, Form, Layout, Radio, Row } from 'antd';
import { CheckboxOptionType, RadioChangeEvent, Spin } from 'antd/lib';
import dayjs from 'dayjs';
import { FC, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { generalConfigApi, reportApi, schedulesApi } from '../../../apis';
import {
  CreateReportDto,
  GetEmployeeTimekeepingHistoryDto,
  Option,
  OptionCategoryEnum,
  TimekeepingReport,
  TimekeepingReportTypeEnum,
  UpdateReportDto,
} from '../../../apis/client-axios';
import ModalWrap from '../../ModalWrap';
import InputWrap from '../../InputWrap';
import { DATE_FORMAT, DATE_FORMAT_JP_2, TIME_FORMAT_JP } from '../../../constants/common';
import DatePickerWrap from '../../DatePickerWrap';
import SelectWrap from '../../SelectWrap';
import { numberDayOffOptions } from '../../../utils';

interface ModalReportProps {
  open: boolean;
  timeKeepingHistory: GetEmployeeTimekeepingHistoryDto;
  timekeepingReport?: TimekeepingReport;
  onCancel: () => void;
  onSubmit: (data: CreateReportDto | UpdateReportDto, id?: string) => void;
  type: TimekeepingReportTypeEnum;
  options: Option[];
  isLoading: boolean;
}

interface ITimekeepingReportData {
  option: Option;
  durationOfLeave?: number;
}

const getFormKey = (key: keyof CreateReportDto | keyof UpdateReportDto) => key;

const dateTimeKeysInReport: (keyof TimekeepingReport)[] = [
  'timeStartOutside',
  'timeBack',
  'timeBackReality',
  'overtimeStart',
  'overtimeEnd',
  'timeEstimated',
  'requestTimekeepingStart',
  'requestTimekeepingEnd',
];

const ModalReport: FC<ModalReportProps> = (props) => {
  const { open, timeKeepingHistory, timekeepingReport, onCancel, onSubmit, type, options, isLoading } = props;
  const intl = useIntl();
  const [form] = Form.useForm();
  const optionId = Form.useWatch('optionId', form);
  const timeStartOutside = Form.useWatch('timeStartOutside', form);
  const optionsByType = options.filter((option) =>
    (
      [
        TimekeepingReportTypeEnum.RequestTimekeepingEdit,
        TimekeepingReportTypeEnum.RequestCheckin,
      ] as TimekeepingReportTypeEnum[]
    ).includes(type)
      ? option.category === OptionCategoryEnum.CheckIn
      : option.category === type,
  );
  const selectedOption = optionsByType.find((option) => option.id === optionId);

  useEffect(() => {
    if (timekeepingReport) {
      const report = { ...timekeepingReport } as { [key: string]: any };
      const data = { ...report.data };
      delete (report as any)?.data;

      for (const key of dateTimeKeysInReport) {
        if (report[key]) {
          report[key] = dayjs(report[key]);
        }
      }

      form.setFieldsValue({
        ...report,
        optionId: (data as ITimekeepingReportData)?.option?.id,
        durationOfLeave: (data as ITimekeepingReportData)?.durationOfLeave,
      });
    }
  }, [timekeepingReport]);

  const handleSubmit = (data: CreateReportDto | UpdateReportDto) => {
    data.type = type;
    data.date = dayjs(timeKeepingHistory.date, DATE_FORMAT_JP_2).format(DATE_FORMAT);
    data.description = data.description || '';
    data.option = selectedOption?.title;
    onSubmit(data, timekeepingReport?.id);
  };

  const renderForm = () => {
    return (
      <Form form={form} layout="vertical" onFinish={handleSubmit}>
        {/* General Information */}
        {optionsByType.length > 0 && (
          <Form.Item
            name={getFormKey('optionId')}
            rules={[{ required: true, message: intl.formatMessage({ id: 'common.required' }) }]}
          >
            <Radio.Group className="d-flex flex-column gap-2">
              {optionsByType.map((option) => (
                <Radio key={option.id} value={option.id}>
                  {option.title}
                </Radio>
              ))}
            </Radio.Group>
          </Form.Item>
        )}
        {selectedOption?.isRequireAdditionalDetail && (
          <Form.Item
            name={getFormKey('description')}
            label={intl.formatMessage({ id: 'timekeepingReports.description.label' })}
            rules={[{ required: true, message: intl.formatMessage({ id: 'common.required' }) }]}
          >
            <InputWrap
              type="textarea"
              maxLength={255}
              placeholder={intl.formatMessage({ id: 'timekeepingReports.description.placeholder' })}
            />
          </Form.Item>
        )}
        {/* Request Timekeeping Edit / Request Checkin / Request Checkout */}
        {(
          [
            TimekeepingReportTypeEnum.RequestTimekeepingEdit,
            TimekeepingReportTypeEnum.RequestCheckin,
            TimekeepingReportTypeEnum.RequestCheckout,
          ] as TimekeepingReportTypeEnum[]
        ).includes(type) && (
          <>
            {type !== TimekeepingReportTypeEnum.RequestCheckout && (
              <Form.Item
                name={getFormKey('requestTimekeepingStart')}
                label={intl.formatMessage({ id: 'timekeepingReports.requestTimekeepingStart.label' })}
                rules={[{ required: true, message: intl.formatMessage({ id: 'common.required' }) }]}
              >
                <DatePickerWrap
                  width="full"
                  showTime={{
                    format: 'HH:mm',
                  }}
                  showNow={false}
                  placeholder={intl.formatMessage({ id: 'timekeepingReports.requestTimekeepingStart.placeholder' })}
                  minDate={dayjs(timeKeepingHistory?.date, DATE_FORMAT_JP_2)}
                  maxDate={dayjs(timeKeepingHistory?.date, DATE_FORMAT_JP_2)}
                  format={TIME_FORMAT_JP}
                />
              </Form.Item>
            )}
            {type !== TimekeepingReportTypeEnum.RequestCheckin && (
              <Form.Item
                name={getFormKey('requestTimekeepingEnd')}
                label={intl.formatMessage({ id: 'timekeepingReports.requestTimekeepingEnd.label' })}
                rules={[{ required: true, message: intl.formatMessage({ id: 'common.required' }) }]}
              >
                <DatePickerWrap
                  width="full"
                  showTime={{
                    format: 'HH:mm',
                  }}
                  showNow={false}
                  placeholder={intl.formatMessage({ id: 'timekeepingReports.requestTimekeepingEnd.placeholder' })}
                  minDate={dayjs(timeKeepingHistory?.date, DATE_FORMAT_JP_2)}
                  maxDate={dayjs(timeKeepingHistory?.date, DATE_FORMAT_JP_2).add(1, 'day')}
                  format={TIME_FORMAT_JP}
                />
              </Form.Item>
            )}
          </>
        )}
        {/* Going Late / Leaving Soon */}
        {(
          [TimekeepingReportTypeEnum.GoingLate, TimekeepingReportTypeEnum.LeavingSoon] as TimekeepingReportTypeEnum[]
        ).includes(type) && (
          <Form.Item
            name={getFormKey('timeEstimated')}
            label={intl.formatMessage({
              id: `timekeepingReports.${type === TimekeepingReportTypeEnum.GoingLate ? 'goingLate' : 'leavingSoon'}.timeEstimated.label`,
            })}
            rules={[{ required: true, message: intl.formatMessage({ id: 'common.required' }) }]}
          >
            <DatePickerWrap
              width="full"
              showTime={{
                format: 'HH:mm',
              }}
              showNow={false}
              placeholder={intl.formatMessage({
                id: `timekeepingReports.${type === TimekeepingReportTypeEnum.GoingLate ? 'goingLate' : 'leavingSoon'}.timeEstimated.placeholder`,
              })}
              minDate={dayjs(timeKeepingHistory?.date, DATE_FORMAT_JP_2)}
              maxDate={dayjs(timeKeepingHistory?.date, DATE_FORMAT_JP_2).add(1, 'day')}
              format={TIME_FORMAT_JP}
            />
          </Form.Item>
        )}
        {/* Overtime */}
        {TimekeepingReportTypeEnum.Overtime === type && (
          <>
            <Form.Item
              name={getFormKey('projectName')}
              label={intl.formatMessage({
                id: `timekeepingReports.overtime.projectName.label`,
              })}
              rules={[{ required: true, message: intl.formatMessage({ id: 'common.required' }) }]}
            >
              <InputWrap
                placeholder={intl.formatMessage({ id: 'timekeepingReports.overtime.projectName.placeholder' })}
              />
            </Form.Item>
            <Form.Item
              name={getFormKey('overtimeEnd')}
              label={intl.formatMessage({
                id: `timekeepingReports.overtime.overtimeEnd.label`,
              })}
              rules={[{ required: true, message: intl.formatMessage({ id: 'common.required' }) }]}
            >
              <DatePickerWrap
                width="full"
                showTime={{
                  format: 'HH:mm',
                }}
                showNow={false}
                placeholder={intl.formatMessage({ id: 'timekeepingReports.overtime.overtimeEnd.placeholder' })}
                minDate={dayjs(timeKeepingHistory?.date, DATE_FORMAT_JP_2)}
                maxDate={dayjs(timeKeepingHistory?.date, DATE_FORMAT_JP_2).add(1, 'day')}
                format={TIME_FORMAT_JP}
              />
            </Form.Item>
          </>
        )}
        {/* Outside Private / Outside Business */}
        {(
          [
            TimekeepingReportTypeEnum.OutsidePrivate,
            TimekeepingReportTypeEnum.OutsideBusiness,
          ] as TimekeepingReportTypeEnum[]
        ).includes(type) && (
          <>
            <Form.Item
              name={getFormKey('timeStartOutside')}
              label={intl.formatMessage({
                id: `timekeepingReports.outside.timeStartOutside.label`,
              })}
              rules={[{ required: true, message: intl.formatMessage({ id: 'common.required' }) }]}
            >
              <DatePickerWrap
                width="full"
                showTime={{
                  format: 'HH:mm',
                }}
                showNow={false}
                placeholder={intl.formatMessage({
                  id: `timekeepingReports.outside.timeStartOutside.placeholder`,
                })}
                minDate={dayjs(timeKeepingHistory?.date, DATE_FORMAT_JP_2)}
                maxDate={dayjs(timeKeepingHistory?.date, DATE_FORMAT_JP_2).add(1, 'day')}
                format={TIME_FORMAT_JP}
              />
            </Form.Item>
            <Form.Item
              name={getFormKey('timeBack')}
              label={intl.formatMessage({
                id: `timekeepingReports.outside.timeBack.label`,
              })}
              rules={[
                { required: true, message: intl.formatMessage({ id: 'common.required' }) },
                {
                  validator: (_, value) => {
                    if (
                      timeStartOutside &&
                      (dayjs(value).isBefore(timeStartOutside) || dayjs(value).isSame(timeStartOutside))
                    ) {
                      return Promise.reject(
                        new Error(intl.formatMessage({ id: 'timekeepingReports.outside.timeBack.validate.error' })),
                      );
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <DatePickerWrap
                width="full"
                showTime={{
                  format: 'HH:mm',
                }}
                showNow={false}
                placeholder={intl.formatMessage({
                  id: `timekeepingReports.outside.timeBack.placeholder`,
                })}
                minDate={dayjs(timeKeepingHistory?.date, DATE_FORMAT_JP_2)}
                maxDate={dayjs(timeKeepingHistory?.date, DATE_FORMAT_JP_2).add(1, 'day')}
                format={TIME_FORMAT_JP}
              />
            </Form.Item>
            <Form.Item
              name={getFormKey('timeBackReality')}
              label={intl.formatMessage({
                id: `timekeepingReports.outside.timeBackReality.label`,
              })}
              rules={[
                { required: true, message: intl.formatMessage({ id: 'common.required' }) },
                {
                  validator: (_, value) => {
                    if (
                      timeStartOutside &&
                      (dayjs(value).isBefore(timeStartOutside) || dayjs(value).isSame(timeStartOutside))
                    ) {
                      return Promise.reject(
                        new Error(
                          intl.formatMessage({ id: 'timekeepingReports.outside.timeBackReality.validate.error' }),
                        ),
                      );
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <DatePickerWrap
                width="full"
                showTime={{
                  format: 'HH:mm',
                }}
                showNow={false}
                placeholder={intl.formatMessage({
                  id: `timekeepingReports.outside.timeBackReality.placeholder`,
                })}
                minDate={dayjs(timeKeepingHistory?.date, DATE_FORMAT_JP_2)}
                maxDate={dayjs(timeKeepingHistory?.date, DATE_FORMAT_JP_2).add(1, 'day')}
                format={TIME_FORMAT_JP}
              />
            </Form.Item>
          </>
        )}
        {/* Request Day Off */}
        {TimekeepingReportTypeEnum.RequestDayOff === type &&
          (selectedOption?.isAnnualLeaveOption || selectedOption?.isCompensatoryLeaveOption) && (
            <Form.Item
              name={getFormKey('durationOfLeave')}
              label={intl.formatMessage({ id: 'timekeepingReports.requestDayOff.durationOfLeave.label' })}
              rules={[{ required: true, message: intl.formatMessage({ id: 'common.required' }) }]}
            >
              <SelectWrap
                options={numberDayOffOptions.map((opt) => ({
                  value: opt.value,
                  label: intl.formatMessage({ id: opt.intlData.id }, opt.intlData.values),
                }))}
              />
            </Form.Item>
          )}
        {/* Request Business */}
        {/* Nothing */}
      </Form>
    );
  };

  return (
    <ModalWrap
      open={open}
      cancelText={intl.formatMessage({ id: 'attendance.confirm.modal.button.cancel' })}
      submitText={intl.formatMessage({ id: 'attendance.confirm.modal.button.submit' })}
      onSubmit={() => form.submit()}
      wrapClassName="attendance-confirm_modal"
      onCancel={onCancel}
      type="upsert"
      title={intl.formatMessage({ id: `timekeepingReports.${type}` })}
      destroyOnClose
      body={<Layout className="bg-white">{renderForm()}</Layout>}
      buttonSubmitProps={{ loading: isLoading }}
    />
  );
};

export default ModalReport;
